import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { Dialog, DIALOG_TYPES, Typography } from '../../alf-design';
import useForm from 'utils/useForm';
import { withApi } from 'utils';
import UserProfileField from './UserProfileField';
import './ResetPasswordModal.css';

const MSG_MISMATCH = 'The passwords do not match.';
const MSG_INVALID_CURRENT_PASSWORD = 'The password is incorrect.';
const INVALID_CURRENT_PASSWORD_API_MESSAGE = 'Invalid current password';

const getPasswordCriteria = (password = '') => ({
  length: password.length >= 8,
  lower: /[a-z]/.test(password),
  upper: /[A-Z]/.test(password),
  number: /[0-9]/.test(password),
  special: /[^a-zA-Z0-9]/.test(password),
});

const countCharacterTypesMet = (c) =>
  [c.lower, c.upper, c.number, c.special].filter(Boolean).length;

const passwordMeetsPolicy = (c) => c.length && countCharacterTypesMet(c) >= 3;

const ResetPasswordModal = ({ show, onClose, currentEmail, api }) => {
  const [showSuccess, toggleSuccess] = useState(false);
  const [showResetPasswordError, setShowResetPasswordError] = useState(false);

  const {
    handleSubmit,
    handleChange,
    setErrors,
    setFormValues,
    values,
    errors,
  } = useForm(
    {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    () => ({}),
    false,
  );

  const [blurred, setBlurred] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  const [mismatchErrorVisible, setMismatchErrorVisible] = useState(false);

  const [criteria, setCriteria] = useState({
    length: false,
    lower: false,
    upper: false,
    number: false,
    special: false,
  });

  useEffect(() => {
    const password = values.newPassword || '';
    setCriteria(getPasswordCriteria(password));
  }, [values.newPassword]);

  const markBlurred = (field) => {
    setBlurred((prev) => ({ ...prev, [field]: true }));
  };

  const evaluateMismatchFromFields = (newPw, confirmPw) => {
    const c = confirmPw || '';
    const n = newPw || '';
    return Boolean(c) && (!n || c !== n);
  };

  const syncMismatchErrorAfterBlur = () => {
    setMismatchErrorVisible(
      evaluateMismatchFromFields(values.newPassword, values.confirmPassword),
    );
  };

  const handleChangeWithReset = (e) => {
    const { name, value } = e.target;
    handleChange(e);
    setShowResetPasswordError(false);

    const nextValues = { ...values, [name]: value };
    if (
      !evaluateMismatchFromFields(
        nextValues.newPassword,
        nextValues.confirmPassword,
      )
    ) {
      setMismatchErrorVisible(false);
    }

    if (value === '') {
      setBlurred((prev) => ({ ...prev, [name]: false }));
      setErrors({});
    } else if (name === 'currentPassword' && errors.currentPassword) {
      setErrors((prev) => {
        const nextErrors = { ...prev };
        delete nextErrors.currentPassword;
        return nextErrors;
      });
    }
  };

  const passwordValid = passwordMeetsPolicy(criteria);
  const threeCharacterTypesMet = countCharacterTypesMet(criteria) >= 3;

  const confirmStr = values.confirmPassword || '';
  const currentStr = values.currentPassword || '';
  const newStr = values.newPassword || '';

  const confirmMismatch =
    Boolean(confirmStr) &&
    (!values.newPassword || confirmStr !== values.newPassword);

  const isButtonDisabled =
    !currentStr || !passwordValid || !newStr || !confirmStr || confirmMismatch;

  const currentPasswordErrorVisible =
    errors.currentPassword === MSG_INVALID_CURRENT_PASSWORD;

  const validationErrorMessages = [
    currentPasswordErrorVisible ? MSG_INVALID_CURRENT_PASSWORD : null,
    mismatchErrorVisible ? MSG_MISMATCH : null,
  ].filter(Boolean);

  const onSubmit = (e) => {
    handleSubmit(e, async (finishSubmitting) => {
      setShowResetPasswordError(false);

      try {
        await api.withAuth.post('/identity/change-password', {
          email: currentEmail,
          currentPassword: values.currentPassword,
          password: values.newPassword,
        });
        resetPasswordForm();
        onClose();
        toggleSuccess(true);
      } catch (err) {
        if (
          err?.response?.status === 400 &&
          err?.response?.data?.Errors?.[0]?.Message ===
            INVALID_CURRENT_PASSWORD_API_MESSAGE
        ) {
          setBlurred((prev) => ({ ...prev, currentPassword: true }));
          setErrors((prev) => ({
            ...prev,
            currentPassword: MSG_INVALID_CURRENT_PASSWORD,
          }));
        } else {
          setShowResetPasswordError(true);
        }
      } finally {
        if (finishSubmitting) finishSubmitting();
      }
    });
  };

  const resetPasswordForm = () => {
    setFormValues(
      {
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      },
      false,
    );
    setCriteria({
      length: false,
      lower: false,
      upper: false,
      number: false,
      special: false,
    });
    setErrors({});
    setBlurred({
      currentPassword: false,
      newPassword: false,
      confirmPassword: false,
    });
    setMismatchErrorVisible(false);
    setShowResetPasswordError(false);
  };

  const handleOnClose = () => {
    resetPasswordForm();
    onClose();
  };

  const characterTypeCriteria = [
    { key: 'lower', label: 'Lower case letters (a-z)' },
    { key: 'upper', label: 'Upper case letters (A-Z)' },
    { key: 'number', label: 'Numbers (0-9)' },
    {
      key: 'special',
      label: 'Special characters (e.g. !@#$%^&*)',
    },
  ];

  const criterionRowClass = (met) =>
    clsx(
      'flex items-start gap-[8px] py-[3px] font-inter text-[12px] font-normal leading-snug',
      met ? 'text-[#16a34a]' : 'text-[#4A5565]',
    );

  const criterionMarkerClass = (met) =>
    clsx(
      'flex h-[14px] w-[14px] shrink-0 select-none items-center justify-center rounded-sm text-[12px] leading-none',
      met ? 'font-semibold text-[#16a34a]' : 'font-normal text-[#9CA3AF]',
    );

  return (
    <>
      <Dialog
        id="reset-password-modal"
        type={DIALOG_TYPES.CONTENT}
        show={show}
        onClose={handleOnClose}
        title="Reset Password"
        subtitle="Create a strong password to protect your account."
        primaryButtonText="Update Password"
        onPrimaryClick={onSubmit}
        disabledPrimaryBtn={isButtonDisabled}
        ariaLabel="Reset password"
      >
        <div className="reset-password-modal-fields flex flex-col font-inter">
          {showResetPasswordError && (
            <div
              className="reset-password-error-banner rounded-[8px] border border-solid border-legacy-error bg-legacy-errorBackground"
              role="alert"
            >
              <Typography type="error" className="!mb-0">
                Uh-Oh! We're unable to process your request at this time. Please
                try again later.
              </Typography>
            </div>
          )}

          <div className="flex flex-col gap-[10px]">
            <UserProfileField
              label="Current Password"
              name="currentPassword"
              type="password"
              compact
              hideErrorMessage
              labelClassName="!font-inter !text-[14px] !font-medium !leading-normal !text-[#364153]"
              value={values.currentPassword}
              touched={blurred.currentPassword || currentPasswordErrorVisible}
              errors={
                currentPasswordErrorVisible ? MSG_INVALID_CURRENT_PASSWORD : ''
              }
              status={currentPasswordErrorVisible ? 'invalid' : ''}
              onChange={handleChangeWithReset}
              onBlur={() => markBlurred('currentPassword')}
              successCriteria={false}
              errorCriteria=""
            />
            <UserProfileField
              label="New Password"
              name="newPassword"
              type="password"
              compact
              hideErrorMessage
              labelClassName="!font-inter !text-[14px] !font-medium !leading-normal !text-[#364153]"
              value={values.newPassword}
              touched={blurred.newPassword}
              errors=""
              status=""
              onChange={handleChangeWithReset}
              onBlur={() => {
                markBlurred('newPassword');
                syncMismatchErrorAfterBlur();
              }}
              successCriteria={
                passwordValid && blurred.newPassword && !confirmMismatch
              }
              errorCriteria=""
            />
            <UserProfileField
              label="Confirm New Password"
              name="confirmPassword"
              type="password"
              compact
              hideErrorMessage
              labelClassName="!font-inter !text-[14px] !font-medium !leading-normal !text-[#364153]"
              value={values.confirmPassword}
              touched={mismatchErrorVisible}
              errors={mismatchErrorVisible ? MSG_MISMATCH : ''}
              status={mismatchErrorVisible ? 'invalid' : ''}
              onChange={handleChangeWithReset}
              onBlur={syncMismatchErrorAfterBlur}
              successCriteria={false}
              errorCriteria=""
            />
          </div>

          <div
            className="box-border rounded-[8px] border border-solid border-legacy-borderLight p-[13px] mt-[20px]"
            aria-live="polite"
          >
            <Typography
              type="caption5"
              component="div"
              className="mb-[8px] text-left !text-[12px]"
            >
              Your password must contain:
            </Typography>
            <ul className="m-0 list-none p-0">
              <li className={criterionRowClass(criteria.length)}>
                <span
                  className={criterionMarkerClass(criteria.length)}
                  aria-hidden
                >
                  {criteria.length ? '✓' : '•'}
                </span>
                <span>At least 8 characters</span>
              </li>
              <li className="list-none py-[2px]">
                <div
                  className={clsx(
                    criterionRowClass(threeCharacterTypesMet),
                    'mb-[6px]',
                  )}
                >
                  <span
                    className={criterionMarkerClass(threeCharacterTypesMet)}
                    aria-hidden
                  >
                    {threeCharacterTypesMet ? '✓' : '•'}
                  </span>
                  <span className="font-medium">
                    At least 3 of the following:
                  </span>
                </div>
                <ul className="m-0 list-none p-0 pl-[22px]">
                  {characterTypeCriteria.map((row) => {
                    const met = criteria[row.key];
                    return (
                      <li key={row.key} className={criterionRowClass(met)}>
                        <span className={criterionMarkerClass(met)} aria-hidden>
                          {met ? '✓' : '•'}
                        </span>
                        <span>{row.label}</span>
                      </li>
                    );
                  })}
                </ul>
              </li>
            </ul>
          </div>

          {validationErrorMessages.length > 0 && (
            <div
              id="reset-password-validation-errors"
              className="mt-[10px] mb-0 box-border flex flex-col gap-[8px]"
            >
              {validationErrorMessages.map((message, index) => (
                <div
                  key={`${message}-${index}`}
                  className="w-full box-border rounded-[8px] border border-solid border-legacy-error bg-legacy-errorBackground p-[13px] text-left"
                  role="alert"
                >
                  <Typography
                    type="error"
                    component="p"
                    className="!mb-0 !text-left"
                  >
                    {message}
                  </Typography>
                </div>
              ))}
            </div>
          )}
        </div>
      </Dialog>
      <Dialog
        id="reset-password-success"
        type={DIALOG_TYPES.CONTENT}
        show={showSuccess}
        onClose={() => toggleSuccess(false)}
        title="Success!"
        primaryButtonText="Close"
        ariaLabel="Password reset success"
      >
        <Typography component="p" className="!mb-[16px]">
          Your password has been updated.
        </Typography>
        <Typography type="caption5" component="p" className="!mb-[22px]">
          Please use your new password the next time you log in.
        </Typography>
      </Dialog>
    </>
  );
};

ResetPasswordModal.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func,
  currentEmail: PropTypes.string,
};

export default withApi(ResetPasswordModal);
